import { addDays, addMinutes } from 'date-fns';
import { objectTotal } from '@travelity/utils';
import {
    EventAsset,
    EventItem,
    EventItemMinimal,
    EventProduct,
    EventStaff,
    UseEventsLazyData,
} from './event.types';
import {
    EventStatus,
    GetEventRes1Dto,
    GetEventResDto,
    GetEventsResItem0Dto,
    type GetEventsResItem0OperationsAssetsItemDto,
    GetEventsResItem0OperationsStaffItemDto,
    type GetEventsResItem0ProductDto,
    GetEventsResItem1Dto,
    GetEventsResItem1OperationsAssetsItemDto,
    GetEventsResItem1OperationsStaffItemDto,
    ProductType,
    TransferEventAssetType,
    type UpdateEventAssetsReqItemDto,
    UpdateEventStaffReqItemDto,
    TransferEventStaffMemberPosition,
} from '../../generated';
import {
    convertActivityDtoToActivity,
    convertDateToTimestamp,
    convertPeriodDataToPeriod,
    convertTimeOffsetToMinutes,
    convertTimestampToDate,
    getStatusChangeReason,
    phoneNumberDataToPhoneNumber,
} from '../common.converters';
import { convertNameDtoToFullName } from '../user/user.converters';
import { getBookingDtoToBooking } from '../booking/booking.converters';
import {
    AvailableEvent,
    AvailableProduct,
} from '../availability/availability.types';

// export const eventToGetEventItemDto = (
//     event: EventItem
// ): Omit<GetEventsResItemDto, 'created'> => ({
//     id: event.id,
//     product: event.product,
//     operations: {
//         capacity_selection: event.capacitySelection,
//     },
//     date: {
//         tz_name: Intl.DateTimeFormat().resolvedOptions()
//             .timeZone as TimezoneName,
//         start: Math.round(event.date.start.getTime() / 1000),
//         end: event.date.end
//             ? Math.round(event.date.end.getTime() / 1000)
//             : undefined,
//     },
//     bookings_summary: event.bookingsSummary,
//     notes: event.notes,
//     status: event.status,
//     // available_seats: event.availableSeats,
//     // ephemeral: event.ephemeral,
// });

export const eventAssetDtoToEventAsset = (
    asset:
        | GetEventsResItem0OperationsAssetsItemDto
        | GetEventsResItem1OperationsAssetsItemDto
): EventAsset => ({
    id: asset.id,
    pinned: asset.pinned,
    reference: asset.reference
        ? {
              id: asset.reference.id,
              type: asset.reference.vehicle_type,
              licensePlate: asset.reference.license_plate,
              make: asset.reference.make,
              model: asset.reference.model,
              maxPassengers: asset.reference.max_passengers,
          }
        : undefined,
});

export const eventAssetToEventAssetDto = (
    asset: EventAsset
): Omit<UpdateEventAssetsReqItemDto, 'action'> => ({
    id: asset.id,
    pinned: asset.pinned,
    reference: asset.reference
        ? {
              type: TransferEventAssetType.VEHICLE,
              id: asset.reference.id,
              vehicle_type: asset.reference.type,
              license_plate: asset.reference.licensePlate,
              make: asset.reference.make,
              model: asset.reference.model,
              max_passengers: asset.reference.maxPassengers,
          }
        : undefined,
});

export const eventStaffDtoToEventStaff = (
    staff:
        | GetEventsResItem0OperationsStaffItemDto
        | GetEventsResItem1OperationsStaffItemDto
): EventStaff => ({
    id: staff.id,
    pinned: staff.pinned,
    position: staff.position,
    reference: staff.reference
        ? {
              id: staff.reference.id,
              fullName: staff.reference.name
                  ? convertNameDtoToFullName(staff.reference.name)
                  : undefined,
              email: staff.reference.contact_details?.emails?.[0] || '',
              phoneNumber: staff.reference.contact_details?.phone_numbers?.[0]
                  ? phoneNumberDataToPhoneNumber(
                        staff.reference.contact_details.phone_numbers[0]
                    )
                  : undefined,
          }
        : undefined,
});

export const eventStaffToEventStaffDto = (
    staff: EventStaff
): Omit<UpdateEventStaffReqItemDto, 'action'> => ({
    id: staff.id || staff.reference?.id,
    pinned: false,
    position: staff.position as TransferEventStaffMemberPosition,
    // reference: staff.reference
    //     ? {
    //           type: UpdateMembersTeamsReqItemReferenceDto.type.PERSON,
    //           id: staff.reference.id,
    //           name: staff.reference.fullName
    //               ? convertFullNameToNameDto(staff.reference.fullName)
    //               : undefined,
    //       }
    //     : undefined,
});

export const eventProductReferenceToProductMinimal = (
    product: GetEventsResItem0ProductDto
): EventProduct => ({
    id: product.id,
    type: product.type as unknown as ProductType,
    name: product.name,
    shared: product.configuration.shared,
});

type EventDto =
    | GetEventsResItem0Dto
    | GetEventsResItem1Dto
    | GetEventResDto
    | GetEventRes1Dto;
export const availableEventToEvent = (
    event: AvailableEvent,
    product: AvailableProduct
): EventItemMinimal => ({
    id: event.id,
    start: event.time,
    productId: product.id,
    productName: product.name,
    productType: product.type,
    shared: product.shared,
    vacant:
        event.vacantSeats !== undefined
            ? event.vacantSeats
            : product.vacantSeats,
    capacityId: product.capacity.id,
    capacityName: product.capacity.name,
});

export const getEventItemDtoToEvent = (event: EventDto): EventItem => ({
    id: event.id,
    locked: event.locked,
    status: event.status,
    date: convertPeriodDataToPeriod(event.date),
    productOptions: event.operations?.product_options || [],
    reason:
        event.status === EventStatus.CANCELLED
            ? getStatusChangeReason(event.lifecycle, EventStatus.CANCELLED)
            : undefined,
    lifecycle: event.lifecycle?.map(convertActivityDtoToActivity) || [],
    bookings: event.bookings.map(getBookingDtoToBooking),
    assets:
        event.operations?.assets?.items?.map(eventAssetDtoToEventAsset) || [],
    staff: event.operations?.staff?.items.map(eventStaffDtoToEventStaff) || [],
    sharedTeams:
        event.access?.groups?.items.map(group => ({
            id: group.id,
            type: group.reference.type,
        })) || [],
    route: event.operations?.route
        ? {
              round_trip: event.operations.route.round_trip,
              items: event.operations?.route.items.map(item => ({
                  ...item,
                  time: convertTimestampToDate(
                      event.date.start +
                          (item.type === 'startpoint'
                              ? 0
                              : convertTimeOffsetToMinutes(
                                    event.operations!.route!.round_trip &&
                                        item.type === 'endpoint'
                                        ? event.date.duration
                                        : item.schedule.arrival_offset
                                ) * 60)
                  ),
              })),
          }
        : undefined,
    capacity: {
        id: event.product?.capacity.reference.id,
        name: event.product?.capacity.reference.name,
    },
    product: eventProductReferenceToProductMinimal(event.product),
    notes: event.notes?.items.map(item => ({
        id: item.id,
        content: item.content,
        visibility: item.visibility,
    })),
    totalPax: objectTotal(event.operations?.pax),
    pax: event.operations?.pax,
    vacant: event.operations?.vacant,
    overbooked: event.operations?.overbooked,
    permissions: {
        canEdit: !!event.access.permissions.update,
        canDelete: !!event.access.permissions.delete,
    },
});

export const eventsFilterToRequestBodyConverter = (
    filters: Record<string, any>
): UseEventsLazyData => ({
    startTs: filters?.dates?.startDate
        ? Math.round(filters.dates.startDate.getTime() / 1000)
        : undefined,
    endTs: filters?.dates?.endDate
        ? Math.round(
              addMinutes(addDays(filters.dates.endDate, 1), -1).getTime() / 1000
          )
        : undefined,
    types: filters.type ? [filters.type] : undefined,
    statuses: filters.statuses,
    text_search: filters.searchText || undefined,
    ...((!!filters.createdAt || !!filters.createdBy) && {
        action: 'created',
        at_start: filters.createdAt?.startDate
            ? convertDateToTimestamp(filters.createdAt.startDate, true)
            : undefined,
        at_end: filters.createdAt?.endDate
            ? convertDateToTimestamp(
                  addMinutes(addDays(filters.createdAt.endDate, 1), -1),
                  true
              )
            : undefined,
        by: filters.createdBy || undefined,
    }),
    ...((!!filters.updatedAt || !!filters.updatedBy) && {
        action: 'last_updated',
        at_start: filters.updatedAt?.startDate
            ? convertDateToTimestamp(filters.updatedAt.startDate, true)
            : undefined,
        at_end: filters.updatedAt?.endDate
            ? convertDateToTimestamp(
                  addMinutes(addDays(filters.updatedAt.endDate, 1), -1),
                  true
              )
            : undefined,
        by: filters.updatedBy || undefined,
    }),
    ...((!!filters.deletedAt || !!filters.deletedBy) && {
        action: 'deleted',
        at_start: filters.deletedAt?.startDate
            ? convertDateToTimestamp(filters.deletedAt.startDate, true)
            : undefined,
        at_end: filters.deletedAt?.endDate
            ? convertDateToTimestamp(
                  addMinutes(addDays(filters.deletedAt.endDate, 1), -1),
                  true
              )
            : undefined,
        by: filters.deletedBy || undefined,
    }),
});
